/**
 * Broker-agnostic backend API URLs. The server picks TradeStation Direct vs Connect Trade
 * from the user's active broker connection — clients use /broker/*, not /tradestation/*.
 *
 * TradeStation Direct OAuth remains at /tradestation/callback, /tradestation/credentials, etc.
 */

const backendUrl = () => process.env.REACT_APP_BACKEND_URL;

/** Order flow (classified buy/sell volume per price). Omit `date` for the live session. */
export const brokerOrderFlowUrl = (symbol, date = null) => {
  let url = `${backendUrl()}/broker/orderflow/${encodeURIComponent(symbol)}`;
  if (date) url += `?date=${encodeURIComponent(date)}`;
  return url;
};

export const brokerOrderFlowDatesUrl = (symbol) =>
  `${backendUrl()}/broker/orderflow/${encodeURIComponent(symbol)}/dates`;

export const brokerAccountsUrl = (paperTrading) =>
  `${backendUrl()}/broker/accounts?paperTrading=${paperTrading}`;

export const brokerBalancesUrl = (accountId, paperTrading, connectionId = null, adapterType = null, { force = false } = {}) => {
  let url = `${backendUrl()}/broker/accounts/${accountId}/balances?paperTrading=${paperTrading}`;
  if (connectionId) url += `&connectionId=${encodeURIComponent(connectionId)}`;
  if (adapterType) url += `&adapterType=${encodeURIComponent(adapterType)}`;
  if (force) url += '&force=true';
  return url;
};

export const brokerPositionsUrl = (accountId, paperTrading, connectionId = null, adapterType = null) => {
  let url = `${backendUrl()}/broker/accounts/${accountId}/positions?paperTrading=${paperTrading}`;
  if (connectionId) url += `&connectionId=${encodeURIComponent(connectionId)}`;
  if (adapterType) url += `&adapterType=${encodeURIComponent(adapterType)}`;
  return url;
};

export const brokerOrdersUrl = (accountId, paperTrading, connectionId = null, adapterType = null) => {
  let url = `${backendUrl()}/broker/accounts/${accountId}/orders?paperTrading=${paperTrading}`;
  if (connectionId) url += `&connectionId=${encodeURIComponent(connectionId)}`;
  if (adapterType) url += `&adapterType=${encodeURIComponent(adapterType)}`;
  return url;
};

export const brokerHistoricalOrdersUrl = (accountId, paperTrading, since, pageSize = 600, nextToken = null, endDate = null) => {
  let url = `${backendUrl()}/broker/accounts/${accountId}/historicalorders?paperTrading=${paperTrading}&pageSize=${pageSize}`;
  if (since) url += `&since=${encodeURIComponent(since)}`;
  // endDate anchors the query window for cursor pagination — sent on every page including cursor requests.
  if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
  if (nextToken) url += `&nextToken=${encodeURIComponent(nextToken)}`;
  return url;
};

export const brokerSymbolSuggestUrl = (text) =>
  `${backendUrl()}/broker/marketdata/symbols/suggest/${encodeURIComponent(text.trim())}`;

export const brokerTickerDetailsUrl = (ticker) =>
  `${backendUrl()}/broker/marketdata/symbols/${ticker}`;

export const brokerBarChartsUrl = (ticker, queryParams) => {
  const qs = new URLSearchParams(queryParams);
  return `${backendUrl()}/broker/marketdata/barcharts/${encodeURIComponent(ticker)}?${qs.toString()}`;
};

export const brokerOptionExpirationsUrl = (underlying, accountId = null, connectionId = null, adapterType = null) => {
  const qs = new URLSearchParams(withBrokerAccountQuery({}, accountId, connectionId, adapterType));
  const query = qs.toString();
  const path = `${backendUrl()}/broker/marketdata/options/expirations/${encodeURIComponent(underlying)}`;
  return query ? `${path}?${query}` : path;
};

export const brokerOptionStrikesUrl = (underlying, expiration, accountId = null, connectionId = null, adapterType = null) => {
  const qs = new URLSearchParams(withBrokerAccountQuery(
    { expiration: String(expiration || '').slice(0, 10) },
    accountId,
    connectionId,
    adapterType
  ));
  return `${backendUrl()}/broker/marketdata/options/strikes/${encodeURIComponent(underlying)}?${qs.toString()}`;
};

/** Attach selected-account routing so MD/brokerage use that broker (not a sibling "active" row). */
export const withBrokerAccountQuery = (queryParams, accountId, connectionId = null, adapterType = null) => {
  const next = { ...(queryParams || {}) };
  if (accountId) next.accountId = String(accountId);
  if (connectionId) next.connectionId = String(connectionId);
  if (adapterType) next.adapterType = String(adapterType);
  return next;
};

export const brokerQuoteSnapshotsUrl = (symbols, accountId = null, connectionId = null, adapterType = null) => {
  const qs = new URLSearchParams(
    withBrokerAccountQuery({ symbols: String(symbols || '') }, accountId, connectionId, adapterType)
  );
  return `${backendUrl()}/broker/marketdata/quotes?${qs.toString()}`;
};

export const brokerQuotesStreamUrl = (symbols) => {
  const symbolsParam = encodeURIComponent(symbols);
  return `${backendUrl()}/broker/stream/quotes?symbols=${symbolsParam}`;
};

export const brokerMarketAggregatesStreamUrl = (ticker) =>
  `${backendUrl()}/broker/marketdata/stream/marketdepth/aggregates/${ticker}`;

export const brokerCreateOrderUrl = (paperTrading) =>
  `${backendUrl()}/broker/orders?paperTrading=${paperTrading}`;

export const brokerOrderUrl = (orderId, paperTrading, accountId = null) => {
  let url = `${backendUrl()}/broker/orders/${orderId}?paperTrading=${paperTrading}`;
  if (accountId) url += `&accountId=${encodeURIComponent(accountId)}`;
  return url;
};

export const brokerOrderGroupUrl = (paperTrading) =>
  `${backendUrl()}/broker/ordergroups?paperTrading=${paperTrading}`;

export const brokerCapabilitiesUrl = (accountId = null, connectionId = null) => {
  let url = `${backendUrl()}/broker/capabilities`;
  const qs = new URLSearchParams();
  if (accountId) qs.set('accountId', String(accountId));
  if (connectionId) qs.set('connectionId', String(connectionId));
  const q = qs.toString();
  return q ? `${url}?${q}` : url;
};
